import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Stage, Layer, Group, Line, Rect, Image } from "react-konva";
import Konva from "konva";
import useImage from "use-image";
import { azureSASTokenToUrl } from "../formatted/FormattedAzureSasToken";

const DEFAULT_MARGIN = 100;
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const SPOTLIGHT_OVERLAY_OPACITY = 0.52;
const SPOTLIGHT_BLUR_RADIUS = 14;
// Extra clear halo around the polygon (~10% of the object's longest side).
const SPOTLIGHT_CLEAR_PADDING_RATIO = 0.1;

const WasteToolbarThumbnail = forwardRef(
  (
    {
      subregion,
      image_url,
      maxWidth,
      maxHeight,
      showSegmentation = true,
      strokeWidth = 2,
      fillRectangle = false,
      margin = DEFAULT_MARGIN,
      marginRatio,
      squareCrop = false,
      // "stroke" = colored outline (default). "spotlight" = soft outer vignette
      // with a clear object area (used on activity material zoom thumbnails).
      highlightMode = "stroke",
    },
    ref,
  ) => {
    const stageRef = useRef();
    const spotlightMaskRef = useRef(null);
    const [image] = useImage(azureSASTokenToUrl(image_url), "anonymous");
    // Spotlight needs a post-mount cache() before the first visible paint.
    const [isSpotlightReady, setIsSpotlightReady] = useState(false);

    useImperativeHandle(ref, () => stageRef.current);

    let x_points = [];
    let y_points = [];
    for (let i = 0; i < subregion.points_konva.length; i++) {
      if (i % 2 === 0) {
        x_points.push(subregion.points_konva[i]);
      } else {
        y_points.push(subregion.points_konva[i]);
      }
    }

    const objectXMin = Math.min(...x_points);
    const objectXMax = Math.max(...x_points);
    const objectYMin = Math.min(...y_points);
    const objectYMax = Math.max(...y_points);
    const objectWidth = objectXMax - objectXMin;
    const objectHeight = objectYMax - objectYMin;
    const proportionalMargin =
      marginRatio != null
        ? Math.max(objectWidth, objectHeight) * marginRatio
        : margin;

    let x_min = objectXMin - proportionalMargin;
    let x_max = objectXMax + proportionalMargin;
    let y_min = objectYMin - proportionalMargin;
    let y_max = objectYMax + proportionalMargin;

    if (squareCrop) {
      const centerX = (objectXMin + objectXMax) / 2;
      const centerY = (objectYMin + objectYMax) / 2;
      const imageWidth = image?.naturalWidth || image?.width;
      const imageHeight = image?.naturalHeight || image?.height;
      let cropSize = Math.max(x_max - x_min, y_max - y_min);

      if (imageWidth && imageHeight) {
        cropSize = Math.min(cropSize, imageWidth, imageHeight);
        x_min = clamp(centerX - cropSize / 2, 0, imageWidth - cropSize);
        y_min = clamp(centerY - cropSize / 2, 0, imageHeight - cropSize);
      } else {
        x_min = centerX - cropSize / 2;
        y_min = centerY - cropSize / 2;
      }

      x_max = x_min + cropSize;
      y_max = y_min + cropSize;
    }

    const cropW = x_max - x_min;
    const cropH = y_max - y_min;

    let stageWidth, stageHeight, scale, groupX, groupY;
    if (maxWidth != null && maxHeight != null && cropW > 0 && cropH > 0) {
      scale = Math.min(maxWidth / cropW, maxHeight / cropH);
      if (fillRectangle) {
        stageWidth = maxWidth;
        stageHeight = maxHeight;
        groupX = (maxWidth - cropW * scale) / 2 - x_min * scale;
        groupY = (maxHeight - cropH * scale) / 2 - y_min * scale;
      } else {
        stageWidth = Math.round(cropW * scale);
        stageHeight = Math.round(cropH * scale);
        groupX = -x_min * scale;
        groupY = -y_min * scale;
      }
    } else {
      const size = 150;
      scale = size / cropW;
      stageWidth = size;
      stageHeight = Math.round((cropH * size) / cropW);
      groupX = -x_min * scale;
      groupY = -y_min * scale;
    }

    const strokeColor = subregion.color || "#00A7A6";
    const useSpotlight = highlightMode === "spotlight";
    const pointsKey = Array.isArray(subregion.points_konva)
      ? subregion.points_konva.join(",")
      : "";

    useEffect(() => {
      if (!image) {
        setIsSpotlightReady(false);
        return undefined;
      }
      if (!useSpotlight) {
        setIsSpotlightReady(true);
        return undefined;
      }
      if (!spotlightMaskRef.current) return undefined;

      const maskNode = spotlightMaskRef.current;
      maskNode.cache();
      maskNode.getLayer()?.batchDraw();
      setIsSpotlightReady(true);
      return () => {
        maskNode.clearCache();
        setIsSpotlightReady(false);
      };
    }, [useSpotlight, image, stageWidth, stageHeight, cropW, cropH, pointsKey]);

    // Wait for the image so square crop + spotlight land in their final
    // position in one paint (avoids a tiny/offset mask then a jump).
    if (!image || cropW <= 2 || cropH <= 2) {
      return null;
    }

    const isVisible = !useSpotlight || isSpotlightReady;

    return (
      <div
        style={{
          width: stageWidth,
          height: stageHeight,
          opacity: isVisible ? 1 : 0,
        }}
      >
        <Stage ref={stageRef} width={stageWidth} height={stageHeight}>
          <Layer>
            <Group
              clip={{
                x: x_min,
                y: y_min,
                width: cropW,
                height: cropH,
              }}
              x={groupX}
              y={groupY}
              scaleX={scale}
              scaleY={scale}
            >
              <Image image={image} listening={false} />

              {useSpotlight ? (
                <Group
                  ref={spotlightMaskRef}
                  listening={false}
                  filters={[Konva.Filters.Blur]}
                  blurRadius={SPOTLIGHT_BLUR_RADIUS / Math.max(scale, 0.001)}
                >
                  <Rect
                    x={x_min}
                    y={y_min}
                    width={cropW}
                    height={cropH}
                    fill={`rgba(0, 0, 0, ${SPOTLIGHT_OVERLAY_OPACITY})`}
                    listening={false}
                  />
                  <Line
                    points={subregion.points_konva}
                    closed
                    fill="#000000"
                    stroke="#000000"
                    strokeWidth={
                      Math.max(objectWidth, objectHeight) *
                      SPOTLIGHT_CLEAR_PADDING_RATIO
                    }
                    lineJoin="round"
                    lineCap="round"
                    globalCompositeOperation="destination-out"
                    listening={false}
                  />
                </Group>
              ) : (
                <>
                  <Rect
                    x={x_min}
                    y={y_min}
                    width={cropW}
                    height={cropH}
                    fill="black"
                    opacity={0}
                  />
                  <Line
                    points={subregion.points_konva}
                    opacity={1.0}
                    stroke={strokeColor}
                    strokeWidth={strokeWidth}
                    strokeScaleEnabled={false}
                    closed
                    visible={showSegmentation}
                  />
                </>
              )}
            </Group>
          </Layer>
        </Stage>
      </div>
    );
  },
);

export default WasteToolbarThumbnail;
